<template>
  <div>
    <!-- Page Header -->
    <b-card>
      <b-row>
        <b-col
          cols="12"
          md="8"
        >
          <h3 class="mb-1">
            {{ $t('settings.coordinates.title') }}
          </h3>
          <b-card-text class="mb-0">
            {{ $t('settings.coordinates.description') }}
          </b-card-text>
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="text-md-right"
        >
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="loading"
            @click="loadAllSettings"
          >
            <feather-icon
              icon="RefreshCwIcon"
              class="mr-50"
            />
            {{ $t('common.refresh') }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Main Settings Tabs -->
    <b-card class="mt-2">
      <b-tabs
        v-model="activeTab"
        pills
        vertical
        nav-class="nav-left"
      >
        <!-- UTM Settings Tab -->
        <b-tab
          :title="$t('settings.coordinates.tabs.utm')"
          active
        >
          <utm-settings-form
            :settings="utmSettings"
            :loading="loading"
            @save="handleSaveUTM"
          />
        </b-tab>

        <!-- ETM Settings Tab -->
        <b-tab :title="$t('settings.coordinates.tabs.etm')">
          <etm-settings-form
            :settings="etmSettings"
            :loading="loading"
            @save="handleSaveETM"
          />
        </b-tab>

        <!-- DMS Settings Tab -->
        <b-tab :title="$t('settings.coordinates.tabs.dms')">
          <dms-settings-form
            :settings="dmsSettings"
            :loading="loading"
            @save="handleSaveDMS"
          />
        </b-tab>

        <!-- WGS84 Parameters Tab -->
        <b-tab :title="$t('settings.coordinates.tabs.wgs84')">
          <wgs84-parameters-form
            :parameters="wgs84Parameters"
            :loading="loading"
            @save="handleSaveWGS84"
          />
        </b-tab>

        <!-- Conversion Factors Tab -->
        <b-tab :title="$t('settings.coordinates.tabs.conversion')">
          <conversion-factors-form
            :factors="conversionFactors"
            :loading="loading"
            @save="handleSaveConversion"
          />
        </b-tab>

        <!-- Egypt Settings Tab -->
        <b-tab :title="$t('settings.coordinates.tabs.egypt')">
          <egypt-settings-form
            :settings="egyptSettings"
            :loading="loading"
            @save="handleSaveEgypt"
          />
        </b-tab>

        <!-- Validation & Display Tab -->
        <b-tab :title="$t('settings.coordinates.tabs.validation')">
          <validation-settings-form
            :grouped-settings="groupedSettings"
            :loading="loading"
            @save="handleSaveValidation"
          />
        </b-tab>

        <!-- Conversion Tools Tab -->
        <b-tab :title="$t('settings.coordinates.tabs.tools')">
          <conversion-tools
            :loading="loading"
          />
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BTabs,
  BTab,
  BRow,
  BCol,
  BButton,
} from 'bootstrap-vue'
import { mapState, mapGetters, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// Import child components
import UtmSettingsForm from './components/UtmSettingsForm.vue'
import EtmSettingsForm from './components/EtmSettingsForm.vue'
import DmsSettingsForm from './components/DmsSettingsForm.vue'
import Wgs84ParametersForm from './components/Wgs84ParametersForm.vue'
import ConversionFactorsForm from './components/ConversionFactorsForm.vue'
import EgyptSettingsForm from './components/EgyptSettingsForm.vue'
import ValidationSettingsForm from './components/ValidationSettingsForm.vue'
import ConversionTools from './components/ConversionTools.vue'

export default {
  name: 'CoordinatesSettings',
  components: {
    BCard,
    BCardText,
    BTabs,
    BTab,
    BRow,
    BCol,
    BButton,
    UtmSettingsForm,
    EtmSettingsForm,
    DmsSettingsForm,
    Wgs84ParametersForm,
    ConversionFactorsForm,
    EgyptSettingsForm,
    ValidationSettingsForm,
    ConversionTools,
  },
  data() {
    return {
      activeTab: 0,
    }
  },
  computed: {
    ...mapState('coordinates', {
      loading: state => state.loading,
      error: state => state.error,
    }),
    ...mapGetters('coordinates', [
      'groupedSettings',
      'utmSettings',
      'etmSettings',
      'dmsSettings',
      'wgs84Parameters',
      'conversionFactors',
      'egyptSettings',
    ]),
  },
  async mounted() {
    await this.loadAllSettings()
  },
  methods: {
    ...mapActions('coordinates', [
      'fetchGroupedSettings',
      'bulkUpdateSettings',
      'convertToDMS',
      'convertToDecimal',
      'validateEgyptBounds',
      'convertUnits',
    ]),

    async loadAllSettings() {
      try {
        await this.fetchGroupedSettings()
      } catch (error) {
        this.showError(error.message || this.$t('settings.coordinates.errors.fetch_failed'))
      }
    },

    async handleSaveUTM(settings) {
      try {
        const payload = this.buildBulkUpdatePayload('utm', settings)
        await this.bulkUpdateSettings(payload)
        this.showSuccess(this.$t('settings.coordinates.messages.utm_updated'))
      } catch (error) {
        this.showError(error.message || this.$t('settings.coordinates.errors.update_failed'))
      }
    },

    async handleSaveETM(settings) {
      try {
        const payload = this.buildBulkUpdatePayload('etm', settings)
        await this.bulkUpdateSettings(payload)
        this.showSuccess(this.$t('settings.coordinates.messages.etm_updated'))
      } catch (error) {
        this.showError(error.message || this.$t('settings.coordinates.errors.update_failed'))
      }
    },

    async handleSaveDMS(settings) {
      try {
        const payload = this.buildBulkUpdatePayload('dms', settings)
        await this.bulkUpdateSettings(payload)
        this.showSuccess(this.$t('settings.coordinates.messages.dms_updated'))
      } catch (error) {
        this.showError(error.message || this.$t('settings.coordinates.errors.update_failed'))
      }
    },

    async handleSaveWGS84(parameters) {
      try {
        const payload = this.buildBulkUpdatePayload('wgs84', parameters)
        await this.bulkUpdateSettings(payload)
        this.showSuccess(this.$t('settings.coordinates.messages.wgs84_updated'))
      } catch (error) {
        this.showError(error.message || this.$t('settings.coordinates.errors.update_failed'))
      }
    },

    async handleSaveConversion(factors) {
      try {
        const payload = this.buildBulkUpdatePayload('conversion', factors)
        await this.bulkUpdateSettings(payload)
        this.showSuccess(this.$t('settings.coordinates.messages.conversion_updated'))
      } catch (error) {
        this.showError(error.message || this.$t('settings.coordinates.errors.update_failed'))
      }
    },

    async handleSaveEgypt(settings) {
      try {
        const payload = this.buildBulkUpdatePayload('egypt', settings)
        await this.bulkUpdateSettings(payload)
        this.showSuccess(this.$t('settings.coordinates.messages.egypt_updated'))
      } catch (error) {
        this.showError(error.message || this.$t('settings.coordinates.errors.update_failed'))
      }
    },

    async handleSaveValidation(settings) {
      try {
        const payload = []

        // Validation settings
        if (settings.validation) {
          Object.entries(settings.validation).forEach(([key, value]) => {
            payload.push({
              key: `coordinates.validation.${key}`,
              value: String(value),
            })
          })
        }

        // Display settings
        if (settings.display) {
          Object.entries(settings.display).forEach(([key, value]) => {
            payload.push({
              key: `coordinates.display.${key}`,
              value: String(value),
            })
          })
        }

        // Default system and datum
        if (settings.default_system) {
          payload.push({
            key: 'coordinates.default_system',
            value: settings.default_system,
          })
        }
        if (settings.default_datum) {
          payload.push({
            key: 'coordinates.default_datum',
            value: settings.default_datum,
          })
        }

        await this.bulkUpdateSettings(payload)
        this.showSuccess(this.$t('settings.coordinates.messages.validation_updated'))
      } catch (error) {
        this.showError(error.message || this.$t('settings.coordinates.errors.update_failed'))
      }
    },

    buildBulkUpdatePayload(category, settings) {
      return Object.entries(settings).map(([key, value]) => ({
        key: `coordinates.${category}.${key}`,
        value: String(value),
      }))
    },

    showSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('common.success'),
          text: message,
          variant: 'success',
          icon: 'CheckIcon',
        },
      })
    },

    showError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('common.error'),
          text: message,
          variant: 'danger',
          icon: 'XIcon',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.mr-50 {
  margin-right: 0.5rem;
}

::v-deep .nav-left {
  .nav-item {
    margin-bottom: 0.5rem;

    .nav-link {
      border-radius: 0.357rem;
      padding: 0.786rem 1.5rem;
    }
  }
}
</style>
